import React, { useState, useEffect, useRef, Fragment, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import Swal from 'sweetalert2';

import { ContenidoDinamico } from '../routes/AppRoutes';
import MenuLogica from '../components/MenuLogica';
import logo from '../components/images/logo.png';
import { resolveApiBaseList } from '../utils/apiBase';


/* Submenú reutilizable con control de apertura */
const Submenu = ({ id, icon, label, children, level = 0, openMap, setOpenMap }) => {
  const isOpen = !!openMap[id];

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className={`submenu submenu-level-${level}`}>
      <button
        type="button"
        className="submenu-btn d-flex align-items-center justify-content-between w-100"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={`items-${id}`}
      >
        <span className="d-flex align-items-center gap-2">
          {icon && <i className={`fa ${icon}`}></i>}
          <span>{label}</span>
        </span>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} arrow`}></i>
      </button>

      <div
        id={`items-${id}`}
        className={`submenu-items ${isOpen ? 'open' : ''}`}
        style={{
          display: isOpen ? 'block' : 'none',
          paddingLeft: level > 0 ? 12 : 0
        }}
      >
        {children}
      </div>
    </div>
  );
};

const PaginaDirectora = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [submenuVisible, setSubmenuVisible] = useState(false); // Estado para controlar la visibilidad del submenú de sesión
  
  const [openMap, setOpenMap] = useState({
    
    // abre lo que quieras por defecto
    catalogos: true,
  });

  const toggleSidebar = () => setSidebarVisible(v => !v);
  // Obtener el nombre completo del alumno desde localStorage
  const nombreMostrar = localStorage.getItem('displayName') || 'Usuario'; // Mostrar el nombre completo del alumno o 'Usuario'
  // Función para mostrar/ocultar el submenú de "Cerrar sesión"
  const toggleSubmenu = () => {
    setSubmenuVisible(!submenuVisible); // Alterna la visibilidad del submenú
  };


  const navigate = useNavigate();
  const location = useLocation();
  const redirigido = useRef(false);
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchWithFallback = useCallback(async (path, options = {}, parse = 'json') => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          let message = '';
          try {
            const text = await response.text();
            message = text || `Error ${response.status}`;
            try {
              const parsed = text ? JSON.parse(text) : null;
              if (parsed && typeof parsed === 'object') {
                message = parsed.message || parsed.error || message;
              }
            } catch {
              // mantener mensaje original
            }
          } catch {
            message = `Error ${response.status}`;
          }

          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        if (parse === 'blob') {
          const blob = await response.blob();
          return { data: blob, baseUrl: normalizedBase, response };
        }

        if (parse === 'text') {
          const text = await response.text();
          return { data: text, baseUrl: normalizedBase, response };
        }

        const text = await response.text();
        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase, response };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase, response };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: no se pudo parsear JSON desde ${url}`, parseErr);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Redirige SOLO si estás en /directora exacto
  useEffect(() => {
    if (!redirigido.current) {
      redirigido.current = true;
      if (location.pathname === '/directora') {
        navigate('/directora/inicio', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // Manejo del cierre de sesión
  const handleLogout = () => {
    // Eliminar las credenciales y otros datos del usuario
    localStorage.clear(); // Limpiar localStorage
    // Redirigir al inicio de sesión
    navigate('/login', { replace: true }); // Redirigir al login
  };

  const handleInicioClick = (e) => {
    e.preventDefault();
    setSidebarVisible(false);
    const stamp = Date.now();
    navigate(`/directora/inicio?r=${stamp}`, { replace: false });
  };

  // Cierra sidebar en móvil al navegar
  const onLinkClick = () => {
    if (window.innerWidth < 768) setSidebarVisible(false);
  };

  //Respaldar
  const handleBackupClick = async () => {
    try {
      const { data: blob, response } = await fetchWithFallback('/utilerias/respaldar', { method: 'GET' }, 'blob');
      const url = window.URL.createObjectURL(blob);

      const cd = response?.headers?.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const fallback = `backup_primariaisc_${new Date().toISOString().replace(/[:.]/g, '-')}.sql`;

      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert(`❌ Error al generar el respaldo: ${err.message}`);
    }
  };

  const fileRef = useRef(null);

  const handleRestoreClick = () => {
  if (fileRef.current) fileRef.current.value = ''; // resetea selección anterior
  fileRef.current?.click();
  };

  const handleRestoreFileChange = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // Bloquea .bak desde el frontend también (UI friendly)
  if (/\.bak$/i.test(file.name)) {
    await Swal.fire({
      icon: 'warning',
      title: 'Formato inválido',
      text: 'El archivo .bak es de SQL Server. Para MySQL sube un .sql o .sql.gz.'
    });
    return;
  }

  const fd = new FormData();
  fd.append('backup', file);

  try {
    const { data: text } = await fetchWithFallback('/utilerias/restaurar', {
      method: 'POST',
      body: fd,
    }, 'text');

    await Swal.fire({
      icon: 'success',
      title: 'Restauración exitosa',
      text: (text && text.trim()) || 'La restauración se completó correctamente.',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    });
  } catch (err) {
    console.error(err);
    await Swal.fire({
      icon: 'error',
      title: 'Error al restaurar',
      text: err.message || 'Ocurrió un problema al restaurar la base de datos.'
    });
  }
  };


  return (
    <div className="pagina-directora d-flex" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Hamburguesa móvil */}
      <button className="hamburger-btn d-md-none" onClick={toggleSidebar} aria-label="Abrir menú">
        <i className="fa fa-bars"></i>
      </button>

      {/* Layout principal */}
      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        {/* Topbar */}
        <div className="topbar d-flex justify-content-between align-items-center px-4 py-2">
          <span className="titulo-top d-flex align-items-center" style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            <img src={logo} alt="Logo del Instituto" width="30" height="30" className="me-2" />
            Instituto Silviano Carrillo
          </span>

          {/* Sesión */}
          <div className="submenu">
            <button className="submenu-btn btn-sesion" onClick={toggleSubmenu}>
              <i className="fa fa-user"></i>
              <span>{nombreMostrar}</span> {/* Muestra el nombre completo del alumno */}
              <i className="fa fa-chevron-down arrow"></i>
            </button>

            {/* Submenú con "Cerrar sesión" */}
            {submenuVisible && (
              <div
                style={{
                  display: "block",
                  position: "absolute",
                  top: "100%",       // debajo del botón
                  right: "0",        // alineado a la derecha
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 12px rgba(7, 152, 249, 0.15)",
                  borderRadius: "8px",
                  minWidth: "180px",
                  zIndex: 1000,
                }}
              >
                <button
                  onClick={handleLogout}
                  style={{
                    padding: "10px 16px",
                    color: "#1976d2",
                    border: "none",
                    background: "none",
                    fontSize: "14px",
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseOver={e => (e.currentTarget.style.backgroundColor = "#67c6ff")}
                  onMouseOut={e => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <i className="fa fa-right-from-bracket"></i>
                  <span>Cerrar sesión</span>
                </button>
              </div>
            )}

          </div>



        </div>

        {/* Sidebar principal controlado */}
        <div
          className={`sidebar ${sidebarVisible ? 'active' : ''}`}
          style={{
            zIndex: 1030,            // evita que algo lo tape
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 0px)'
          }}
        >
          <MenuLogica />

          <div className="menu">
            <span className="titulo-left">Plataforma Digital Escolar</span>

            {/* Inicio (enlace directo) */}
            <div className="submenu">
              <Link
                to="/directora/inicio"
                onClick={(e) => { handleInicioClick(e); onLinkClick(); }}
                className="submenu-btn d-flex align-items-center gap-2"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <i className="fa fa-house"></i>
                <span>Inicio</span>
              </Link>
            </div>
            <button className="submenu-btn">
              <i className="fa fa-user"></i>
              <Link to="/directora/consultar_informacion_directora" style={{ all: 'unset', cursor: 'pointer' }}>
                Consultar Información
              </Link>
            </button>

            {/* Catálogos */}
            <Submenu
              id="catalogos"
              icon="fa-folder"
              label="Catálogos"
              level={0}
              openMap={openMap}
              setOpenMap={setOpenMap}
            >
              {/* Alumnos */}
              <Submenu
                id="cat-alumnos"
                icon="fa-user"
                label="Alumnos"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-alumnos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Alumnos</span>
                </Link>
                <Link to="/directora/modificar-alumnos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-check-circle"></i> <span>Modificar Alumnos</span>
                </Link>
                <Link to="/directora/consultar-alumnos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Alumnos</span>
                </Link>
              </Submenu>

              {/* Docentes */}
              <Submenu
                id="cat-docentes"
                icon="fa-chalkboard-teacher"
                label="Docentes"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-docentes" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Docentes</span>
                </Link>
                <Link to="/directora/modificar-docentes" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-check-circle"></i> <span>Modificar Docentes</span>
                </Link>
                <Link to="/directora/consultar-docentes" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Docentes</span>
                </Link>
              </Submenu>

              {/* Coordinador */}
              <Submenu
                id="cat-coordinador"
                icon="fa-user-cog"
                label="Coordinador"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/consultar-coordinador" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar su Información</span>
                </Link>
              </Submenu>

              
              {/* Categoría Materia */}
              <Submenu
                id="cat-categoria"
                icon="fa-file-pen"
                label="Categoría Materia"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-categoria-materias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Categoría</span>
                </Link>
                <Link to="/directora/modificar-categoria" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-check-circle"></i> <span>Modificar Categoría</span>
                </Link>
                <Link to="/directora/consultar-categoria" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Categoría</span>
                </Link>
              </Submenu>

              {/* Materias */}
              <Submenu
                id="cat-materias"
                icon="fa-book"
                label="Materias"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-materias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Materias</span>
                </Link>
                <Link to="/directora/modificar-materias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-check-circle"></i> <span>Modificar Materias</span>
                </Link>
                <Link to="/directora/consultar-materias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Materias</span>
                </Link>
              </Submenu>

              {/* Grupos */}
              <Submenu
                id="cat-grupos"
                icon="fa-user-group"
                label="Grupos"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-grupos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Grupos</span>
                </Link>
                <Link to="/directora/modificar-grupos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-check-circle"></i> <span>Modificar Grupos</span>
                </Link>
                <Link to="/directora/consultar-grupos" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Grupos</span>
                </Link>
              </Submenu>

              {/* Evidencias */}
              <Submenu
                id="cat-evidencias"
                icon="fa-book-open"
                label="Evidencias"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-evidencias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Evidencias</span>
                </Link>
                <Link to="/directora/consultar-evidencias" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Evidencias</span>
                </Link>
              </Submenu>
            </Submenu>

            {/* Movimientos */}
            <Submenu
              id="movimientos"
              icon="fa-right-left"
              label="Movimientos"
              level={0}
              openMap={openMap}
              setOpenMap={setOpenMap}
            >
              {/* Horarios */}
              <Submenu
                id="mov-horarios"
                icon="fa-clock"
                label="Horarios"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-horarios" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Horarios</span>
                </Link>
                <Link to="/directora/consultar-horarios" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Horarios</span>
                </Link>
              </Submenu>

              {/* Calificaciones */}
              <Submenu
                id="mov-calificaciones"
                icon="fa-file-lines"
                label="Calificaciones"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-calificaciones" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Modificar Calificaciones</span>
                </Link>
                <Link to="/directora/registrar_calificacionxgrupo" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Calificaciones por Grupo</span>
                </Link>
                <Link to="/directora/consultar-calificaciones" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Calificaciones por Grupo y Alumno</span>
                </Link>
                <Link to="/directora/consultar-grupos-calificaciones" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-table"></i> <span>Consultar Calificaciones por Grupo y Trimestre</span>
                </Link>
              </Submenu>

              {/* Planeaciones */}
              <Submenu
                id="mov-planeaciones"
                icon="fa-book"
                label="Planeaciones"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-planeaciones" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Planeaciones</span>
                </Link>
                <Link to="/directora/consultar-planeaciones" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Dar Observaciones</span>
                </Link>
              </Submenu>
            </Submenu>

            {/* Utilerías */}
            <Submenu
              id="utilerias"
              icon="fa-wrench"
              label="Utilerías"
              level={0}
              openMap={openMap}
              setOpenMap={setOpenMap}
            >
              <div className="submenu">
                <button className="submenu-btn" type="button" onClick={handleBackupClick}>
                  <i className="fa fa-download"></i> <span>Respaldar</span>
                </button>
              </div>

                <div className="submenu">
                  <button
                    className="submenu-btn"
                    type="button"
                    onClick={handleRestoreClick}   
                  >
                    <i className="fa fa-window-restore"></i> <span>Restaurar</span>
                  </button>
                </div>


              {/* Traspaso Histórico */}
              <Submenu
                id="util-traspaso"
                icon="fa-scroll"
                label="Traspaso Histórico"
                level={1}
                openMap={openMap}
                setOpenMap={setOpenMap}
              >
                <Link to="/directora/registrar-traspaso" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-pen"></i> <span>Registrar Traspaso</span>
                </Link>
                <Link to="/directora/consultar-traspaso" className="submenu-btn" onClick={onLinkClick}>
                  <i className="fa fa-search"></i> <span>Consultar Traspaso</span>
                </Link>
              </Submenu>
            </Submenu>
          </div>
        </div>

        {/* Panel dinámico */}
        <main
          className={`contenido-principal flex-grow-1 p-4 ${!sidebarVisible ? 'contenido-expandido' : ''}`}
          id="contenido-dinamico"
          style={{
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 0px)'
          }}
        >
          <ContenidoDinamico key={location.pathname + location.search} />
        </main>

          <input
            type="file"
            accept=".sql,.gz,.sql.gz,.bak"
            ref={fileRef}
            onChange={handleRestoreFileChange}
            style={{ display: 'none' }}
          />
      </div>
    </div>
  );
};

export default PaginaDirectora;
